import React from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, CircularProgress } from '@mui/material';
import { Description as QueryIcon } from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius, shadows, tableStyles } from '../../constants';
import ProjectHeaderRow from './ProjectHeaderRow';
import EscalatedQueryTableRow from './EscalatedQueryTableRow';

/**
 * EscalatedQueriesTable Component
 * Main table component with header and body
 */
const EscalatedQueriesTable = ({
  loading,
  total,
  paginatedProjects,
  groupedQueriesByProject,
  expandedProjects,
  projectsCatalog = [],
  onToggleProject,
  onView,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  tableContainerRef,
}) => {
  const tableHeaderStyles = {
    ...tableStyles.header,
    borderBottom: `2px solid ${colors.borderDefault}`,
    borderRight: `1px solid ${colors.borderDefault}`,
    '&:last-child': {
      borderRight: 'none',
    },
  };

  const tableBodyCellStyles = {
    ...tableStyles.cell,
    borderBottom: `2px solid ${colors.borderDefault}`,
    borderRight: `1px solid ${colors.borderDefault}`,
    '&:last-child': {
      borderRight: 'none',
    },
  };

  const tableRowStyles = {
    '&:hover': {
      backgroundColor: colors.primaryTint,
    },
    transition: 'background-color 0.2s ease',
  };

  return (
    <Paper
      sx={{
        borderRadius: borderRadius.lg,
        boxShadow: shadows.lg,
        border: `1px solid ${colors.borderLight}`,
        background: colors.backgroundWhite,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: spacing.md,
          py: spacing.sm,
          background: '#1e293b',
          color: colors.textWhite,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: `${borderRadius.md} ${borderRadius.md} 0 0`,
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: '#0e172a',
            filter: 'blur(50px)',
            opacity: 0.6,
            zIndex: 0,
          },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: fonts.weightSemiBold,
            fontSize: fonts.fontSizeSm,
            letterSpacing: '0.02em',
            position: 'relative',
            zIndex: 1,
            textShadow: '0 0 8px rgba(250, 204, 21, 0.4)',
          }}
        >
          Notified Queries {total > 0 && `(${total})`}
        </Typography>
      </Box>
      <TableContainer 
        ref={tableContainerRef}
        sx={{ 
          // border: `2px solid ${colors.borderDefault}`, 
          // borderRadius: borderRadius.md, 
          // borderTop: 'none',
          // borderTopRightRadius: '0',
          // borderTopLeftRadius: '0',
          // borderBottom: 'none',
          // borderBottomRightRadius: '0',
          // borderBottomLeftRadius: '0',
          border: 'none',
          borderTop: `2px solid ${colors.borderDefault}`,
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 400px)',
          minHeight: '400px',
          position: 'relative',
        }}
      >
        <Table 
          sx={{ 
            minWidth: { xs: 800, sm: 960 },
            '& tbody tr:last-child td': {
              borderBottom: 'none !important',
            },
            '& tbody tr:last-child .MuiTableCell-root': {
              borderBottom: 'none !important',
            },
          }}
        >
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              '& th': {
                position: 'sticky',
              },
            }}
          >
            <TableRow sx={{ backgroundColor: '#1e293b' }}>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Query Type</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Query ID</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Status</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Raised By</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Escalated To</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Created Date</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff' }}>Escalation Response time</TableCell>
              <TableCell sx={{ ...tableHeaderStyles, color: '#ffffff', width: '120px', minWidth: '120px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& .MuiTableRow-root:last-of-type td': {
                borderBottom: 'none !important',
              },
              '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
                borderBottom: 'none !important',
              },
              '& .MuiTableRow-root:last-child td': {
                borderBottom: 'none !important',
              },
              '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: 'none !important',
              },
            }}
          >
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ ...tableBodyCellStyles, textAlign: 'center', py: spacing.xl, borderBottom: 'none' }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : total === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ ...tableBodyCellStyles, textAlign: 'center', py: spacing.xl, borderBottom: 'none' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: spacing.sm,
                      py: spacing.xl,
                    }}
                  >
                    <QueryIcon sx={{ fontSize: '48px', color: colors.textTertiary, opacity: 0.3 }} />
                    <Typography
                      sx={{
                        color: colors.textSecondary,
                        fontSize: '0.9375rem',
                        fontWeight: fonts.weightMedium,
                      }}
                    >
                      No projects found
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.textTertiary,
                        fontSize: '0.8125rem',
                        fontWeight: fonts.weightRegular,
                      }}
                    >
                      Try adjusting your filters
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedProjects.map((projectName) => {
                const projectData = groupedQueriesByProject[projectName];
                if (!projectData) return null;
                
                const projectKey = projectName;
                const isExpanded = expandedProjects.has(projectKey);
                const queryCount = projectData.queries.length;
                
                return (
                  <React.Fragment key={projectKey}>
                    <ProjectHeaderRow
                      projectName={projectName}
                      projectsCatalog={projectsCatalog}
                      queryCount={queryCount}
                      isExpanded={isExpanded}
                      onToggle={() => onToggleProject(projectKey)}
                      tableBodyCellStyles={tableBodyCellStyles}
                    />
                    {isExpanded &&
                      projectData.queries.map((query) => (
                        <EscalatedQueryTableRow
                          key={query._id}
                          query={query}
                          onView={onView}
                          tableBodyCellStyles={tableBodyCellStyles}
                          tableRowStyles={tableRowStyles}
                        />
                      ))}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          borderTop: `2px solid ${colors.borderDefault}`,
          borderBottom: `2px solid ${colors.borderDefault}`,
          backgroundColor: colors.backgroundWhite,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: fonts.fontSizeSm,
            color: colors.textPrimaryDark,
          },
          '& .MuiTablePagination-select': {
            cursor: 'pointer',
            '&:hover': {
              cursor: 'pointer',
            },
          },
          '& .MuiTablePagination-selectLabel': {
            cursor: 'default',
          },
          '& .MuiIconButton-root': {
            color: colors.textPrimaryDark,
            '&:hover': {
              backgroundColor: colors.primaryTint,
            },
          },
        }}
      />
    </Paper>
  );
};

export default EscalatedQueriesTable;
